import { useState } from "react";
import { useAuth } from "@shared/hooks/useAuth";
import { useDashboardMetrics } from "@admin/hooks";
import {
  Package,
  ShoppingCart,
  Book,
  Tag,
  TrendingUp,
} from "lucide-react";

// Función para formatear números con K y M
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

// Función para formatear porcentajes
const formatPercentage = (num: number): string => {
  const sign = num >= 0 ? "+" : "";
  return `${sign}${num.toFixed(2)}%`;
};

// Función para formatear fechas
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("es-AR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export function Dashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState<"7d" | "30d" | "12m">("30d");
  const [groupBy, setGroupBy] = useState<"day" | "week" | "month">("day");

  const {
    data: metrics,
    isLoading,
    isError,
    error,
  } = useDashboardMetrics({
    period,
    groupBy,
    topProductsLimit: 10,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            ¡Bienvenido, {user?.fullName || user?.email}!
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Cargando métricas del dashboard...
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 sm:py-6 animate-pulse"
            >
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError || !metrics) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            ¡Bienvenido, {user?.fullName || user?.email}!
          </h1>
          <p className="mt-1 text-sm text-red-500">
            Error al cargar las métricas:{" "}
            {error?.message || "Error desconocido"}
          </p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      name: "Cotizaciones Totales",
      value: formatNumber(metrics.quotes.summary.total),
      change: formatPercentage(metrics.quotes.summary.growthRate),
      changeType: metrics.quotes.summary.growthRate >= 0 ? "positive" : "negative" as const,
      icon: ShoppingCart,
    },
    {
      name: "Productos",
      value: formatNumber(metrics.products.summary.active),
      subtitle: `${metrics.products.summary.inactive} inactivos • ${metrics.products.summary.outstanding} destacados`,
      icon: Package,
    },
    {
      name: "Categorías",
      value: formatNumber(metrics.categories.summary.active),
      subtitle: `${metrics.categories.summary.inactive} inactivas • ${metrics.categories.summary.featured} destacadas`,
      icon: Tag,
    },
    {
      name: "Catálogos",
      value: formatNumber(metrics.catalogs.summary.active),
      subtitle: `${metrics.catalogs.summary.inactive} inactivos • ${metrics.catalogs.summary.featured} destacados`,
      icon: Book,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          ¡Bienvenido, {user?.fullName || user?.email}!
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Aquí tienes un resumen de tu panel de administración
        </p>
      </div>

      {/* Selectores de período y agrupación */}
      <div className="flex flex-wrap gap-4 items-center">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Período
          </label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as "7d" | "30d" | "12m")}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828]"
          >
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="12m">Últimos 12 meses</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Agrupar por
          </label>
          <select
            value={groupBy}
            onChange={(e) =>
              setGroupBy(e.target.value as "day" | "week" | "month")
            }
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828]"
          >
            <option value="day">Día</option>
            <option value="week">Semana</option>
            <option value="month">Mes</option>
          </select>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 sm:py-6"
            >
              <dt>
                <div className="absolute rounded-md bg-[#282828] p-3">
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <p className="ml-16 truncate text-sm font-medium text-gray-500">
                  {stat.name}
                </p>
              </dt>
              <dd className="ml-16 flex items-baseline">
                <p className="text-2xl font-semibold text-gray-900">
                  {stat.value}
                </p>
                {stat.change && (
                  <p
                    className={`ml-2 flex items-baseline text-sm font-semibold ${
                      stat.changeType === "positive"
                        ? "text-[#282828]"
                        : "text-red-600"
                    }`}
                  >
                    {stat.change}
                  </p>
                )}
              </dd>
              {stat.subtitle && (
                <p className="ml-16 mt-1 text-xs text-gray-500">
                  {stat.subtitle}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Contenido principal */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Timeline de Cotizaciones */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Línea de Tiempo de Cotizaciones
            </h3>
            <div className="space-y-3">
              {metrics.quotes.timeline.data.length > 0 ? (
                metrics.quotes.timeline.data.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(item.date)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.count} cotizaciones • {item.totalProducts} productos • {item.totalQuantity} unidades
                      </p>
                    </div>
                    <div className="ml-4">
                      <div
                        className="h-2 bg-[#282828] rounded"
                        style={{
                          width: `${Math.min((item.count / Math.max(...metrics.quotes.timeline.data.map(d => d.count))) * 100, 100)}px`,
                        }}
                      ></div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No hay datos disponibles</p>
              )}
            </div>
          </div>
        </div>

        {/* Top Productos */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Top Productos Cotizados
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Producto
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Veces
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {metrics.quotes.topProducts.length > 0 ? (
                    metrics.quotes.topProducts.map((product) => (
                      <tr key={product.productId}>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          <div>
                            <p className="font-medium">{product.productName}</p>
                            {product.brand && (
                              <p className="text-xs text-gray-500">{product.brand}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {product.timesQuoted}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {formatNumber(product.totalQuantity)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-sm text-gray-500 text-center">
                        No hay productos disponibles
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Métricas adicionales de productos, categorías y catálogos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Métricas de Productos */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Métricas de Productos
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatNumber(metrics.products.summary.total)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Destacados</span>
                <span className="text-sm font-semibold text-gray-900">
                  {metrics.products.summary.outstanding}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Sin Imagen</span>
                <span className="text-sm font-semibold text-orange-600">
                  {metrics.products.summary.withoutImage}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Sin Descripción</span>
                <span className="text-sm font-semibold text-orange-600">
                  {metrics.products.summary.withoutDescription}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Creados (30d)</span>
                <span className="text-sm font-semibold text-blue-600">
                  {metrics.products.summary.recentlyCreated}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Actualizados (30d)</span>
                <span className="text-sm font-semibold text-blue-600">
                  {metrics.products.summary.recentlyUpdated}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Métricas de Categorías */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Tag className="h-5 w-5 mr-2" />
              Métricas de Categorías
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatNumber(metrics.categories.summary.total)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Destacadas</span>
                <span className="text-sm font-semibold text-gray-900">
                  {metrics.categories.summary.featured}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Con Imagen</span>
                <span className="text-sm font-semibold text-green-600">
                  {metrics.categories.summary.withImage}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Sin Imagen</span>
                <span className="text-sm font-semibold text-orange-600">
                  {metrics.categories.summary.withoutImage}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Categorías Raíz</span>
                <span className="text-sm font-semibold text-gray-900">
                  {metrics.categories.summary.rootCategories}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Subcategorías</span>
                <span className="text-sm font-semibold text-gray-900">
                  {metrics.categories.summary.subcategories}
                </span>
              </div>
              <div className="flex items-center justify-between border-t pt-2">
                <span className="text-sm text-gray-600">Promedio Productos/Cat.</span>
                <span className="text-sm font-semibold text-gray-900">
                  {metrics.categories.summary.averageProductsPerCategory.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Métricas de Catálogos */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Book className="h-5 w-5 mr-2" />
              Métricas de Catálogos
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatNumber(metrics.catalogs.summary.total)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Destacados</span>
                <span className="text-sm font-semibold text-gray-900">
                  {metrics.catalogs.summary.featured}
                </span>
              </div>
              <div className="flex items-center justify-between border-t pt-2">
                <span className="text-sm text-gray-600">Promedio Categorías/Cat.</span>
                <span className="text-sm font-semibold text-gray-900">
                  {metrics.catalogs.summary.averageCategoriesPerCatalog.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resumen adicional de cotizaciones */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Resumen de Cotizaciones
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">Hoy</p>
              <p className="text-lg font-semibold text-gray-900">
                {metrics.quotes.summary.today}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Esta Semana</p>
              <p className="text-lg font-semibold text-gray-900">
                {metrics.quotes.summary.thisWeek}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Este Mes</p>
              <p className="text-lg font-semibold text-gray-900">
                {metrics.quotes.summary.thisMonth}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Mes Anterior</p>
              <p className="text-lg font-semibold text-gray-900">
                {metrics.quotes.summary.lastMonth}
              </p>
            </div>
          </div>
          <div className="border-t pt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center justify-between md:flex-col md:items-start">
              <div className="flex items-center space-x-2">
                <Package className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">Total Productos Cotizados</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(metrics.quotes.summary.totalProductsQuoted)}
              </span>
            </div>
            <div className="flex items-center justify-between md:flex-col md:items-start">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">Total Cantidad Cotizada</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(metrics.quotes.summary.totalQuantityQuoted)}
              </span>
            </div>
            <div className="flex items-center justify-between md:flex-col md:items-start">
              <div className="flex items-center space-x-2">
                <ShoppingCart className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">Promedio Productos/Cotización</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {metrics.quotes.summary.averageProductsPerQuote.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between md:flex-col md:items-start">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">Promedio Cantidad/Cotización</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {metrics.quotes.summary.averageQuantityPerQuote.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
